/** @jsx jsx */
import { css, jsx } from "@emotion/core"
import React, { useState, useRef, useEffect } from "react"
import { useMaybeFileboy, API_URL } from "@cd2/fileboy-react"
import {
  createPlayer,
  togglePlay,
  isPlayerReady,
  getCurrentTime,
  IPlayer,
  isBuffering,
  play,
} from "./utils/shaka"
import { useVideoData } from "./hooks/useVideoData"
import { usePlayingStatus } from "./hooks/usePlayingStatus"
import { useOnClickOutside } from "./hooks/useOnClickOutside"
import { useActiveMouseInArea } from "./hooks/useActiveMouseInArea"
import { useVolume } from "./hooks/useVolume"
import { useQualitySettings } from "./hooks/useQualitySettings"
import { CentralPlayerIcon } from "./Components/CentralPlayerIcon"
import { useVideoPosition } from "./hooks/usePlayerPosition"
import { useTextTrack } from "./hooks/useTextTrack"
import { Spinner } from "./Components/Spinner"
import { VideoErrorBoundary } from "./VideoErrorBoundary"
import { Captions } from "./Captions"
import { useFileboyVideo } from "./hooks/useFileboyVideo"
import { ToolTip } from "./Components/ToolTip"
import { getVideoStatus } from "./utils/getVideoStatus"
import { Controls } from "./Components/Controls"

interface IVideoEventInfo {
  currentTime: number
  duration: number
}
interface IVideoPlayProps {
  id: string
  loop?: boolean
  autoPlay?: boolean
  className?: string
  coverImage?: string
  height?: string | number
  onTimeChange?(info: IVideoEventInfo): void
  onPlay?(info: IVideoEventInfo): void
  onPause?(info: IVideoEventInfo): void
  onEnd?(info: IVideoEventInfo): void
  displayControlsPriorToPlay?: boolean
  displayToolTips?: boolean
  preload?: boolean
  onLoad?({
    status,
    data,
  }: {
    status: ReturnType<typeof useVideoData>[1]
    data?: ReturnType<typeof useVideoData>[0]
  }): void
  /**
   * - `undefined` will use the default behavior
   * - `null` will turn off
   * - `string` language code will try to find matching caption by lang code and set that to active
   */
  defaultCaption?: string | null
  displayCaptionControl?: boolean
  apiUrl?: string
  isEmbed?: boolean
}

export function VideoPlayer(props: IVideoPlayProps) {
  return (
    <VideoErrorBoundary>
      <VideoPlayerInner {...props} />
    </VideoErrorBoundary>
  )
}

function VideoPlayerInner({
  id,
  autoPlay = false,
  loop = false,
  className,
  height,
  coverImage,
  onTimeChange,
  displayControlsPriorToPlay = false,
  displayToolTips = true,
  onPlay = () => {},
  onPause = () => {},
  onEnd = () => {},
  onLoad = () => {},
  preload,
  defaultCaption,
  displayCaptionControl = true,
  apiUrl,
  isEmbed,
}: IVideoPlayProps) {
  const fileboy = useMaybeFileboy()
  const resolvedApiUrl = apiUrl || fileboy?.apiUrl || API_URL
  const fileboyVideo = useFileboyVideo(resolvedApiUrl)

  function getManifestUrl(video: { id: string }) {
    return fileboyVideo.manifestUrl(video.id)
  }

  const containerRef = useRef<HTMLDivElement>(null)
  const [isLoaded, setIsLoaded] = React.useState(false)
  const videoRef = React.useRef<HTMLVideoElement>(null)

  const [player, setPlayer] = useState<IPlayer | null>(null)
  const [data, status] = useVideoData(id, resolvedApiUrl)

  const [userHasAttemptedPlay, setUserHasAttemptedPlay] = useState(!!autoPlay)

  useEffect(() => {
    if (isLoaded) {
      return
    }
    const fn = async () => {
      if (!data) {
        return
      }
      if (!preload && !userHasAttemptedPlay) {
        return
      }

      const status = getVideoStatus(data)
      if (status === "Done" || status === "Playable") {
        const player = await createPlayer({
          element: videoRef.current,
          manifestUri: getManifestUrl(data),
          forceUpdate,
          playbackTimeUpdated: onTimeChange,
          videoPaused: onPause,
          videoPlayed: onPlay,
        })
        setPlayer(player)
        setIsLoaded(true)
      }
    }
    fn()
  }, [videoRef.current, data, userHasAttemptedPlay, preload])

  const [, setUpdateKey] = React.useState(1)
  const [activeMouseInArea, { handleMouseEnter, handleMouseLeave }] = useActiveMouseInArea()
  const [activeMenu, setActiveMenu] = useState<"quality" | "captions" | null>(null)
  const [volume] = useVolume(player)
  const [, handleSeek] = useVideoPosition(player)
  const { hasStarted, hasEnded, isPlaying } = usePlayingStatus(player)

  const { availableQualities } = useQualitySettings(player, data)
  const { availableCaptions, currentCaptionId, handleSetCurrentCaptionId } = useTextTrack(
    id,
    {
      defaultCaption,
    },
    resolvedApiUrl,
  )
  const [xPos, setXPos] = useState("0px")
  const [yPos, setYPos] = useState("0px")
  const [showContextMenu, setShowContextMenu] = useState(false)
  const [showDebug, setShowDebug] = useState(false)

  const [userInteractedWithVideo, setUserInteractedWithVideo] = useState(false)
  const userInteractionTimeout = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (userInteractedWithVideo) {
      if (userInteractionTimeout.current) {
        clearTimeout(userInteractionTimeout.current)
      }
      userInteractionTimeout.current = setTimeout(() => setUserInteractedWithVideo(false), 3000)
    }
  }, [userInteractedWithVideo])

  useEffect(() => {
    if (videoRef.current && isLoaded && hasEnded) {
      const { currentTime, duration } = videoRef.current
      onEnd({ currentTime, duration })
    }
  }, [hasEnded, isPlaying])

  useOnClickOutside(containerRef, () => {
    setShowContextMenu(false)
    setActiveMenu(null)
  })

  useEffect(() => {
    if (isLoaded && userHasAttemptedPlay && !isPlaying) {
      play(player)!.catch(() => {
        console.info("User has not interacted with document yet.")
      })
    }
  }, [isLoaded])

  useEffect(() => {
    if (status && status !== "loading" && onLoad) {
      onLoad({ status, data })
    }
  }, [status, data?.id])

  function forceUpdate() {
    setUpdateKey(key => key + 1)
  }

  function handleContextMenu(e: any) {
    e.preventDefault()
    if (containerRef.current) {
      const { x, y } = containerRef.current.getBoundingClientRect()
      setXPos(`${e.clientX - x}px`)
      setYPos(`${e.clientY - y}px`)
      setShowContextMenu(true)
    }
  }

  const errorMessages = {
    errored: "An error has occurred, please try again",
    notFound: "Video not found",
    pending: "Video is being processed, please check back in a few minutes",
    processing: "Video is being processed, please check back in a few minutes",
    error: "An error has occurred processing this video",
  }

  const wontPlayStatus = Object.keys(errorMessages).includes(status)

  const displayOverlay =
    (userInteractedWithVideo || activeMouseInArea || activeMenu || !isPlaying || hasEnded) &&
    !(!displayControlsPriorToPlay && !userHasAttemptedPlay)
  const cover = coverImage ?? (data ? `${resolvedApiUrl}/videos/${data.id}/poster` : undefined)

  function handleClick() {
    setUserHasAttemptedPlay(true)
    togglePlay(player)
    setUserInteractedWithVideo(true)
    setShowContextMenu(false)
  }

  return (
    <div
      ref={containerRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={() => {
        handleMouseLeave()
        setUserInteractedWithVideo(false)
      }}
      onContextMenu={handleContextMenu}
      onTouchMove={() => setUserInteractedWithVideo(true)}
      css={css`
        all: unset;
        display: block;
        font-family: -apple-system, BlinkMacSystemFont, Segoe UI, Helvetica, Arial, sans-serif,
          Apple Color Emoji, Segoe UI Emoji;
        -webkit-font-smoothing: antialiased;
        position: relative;
        background-color: black;
        width: 100%;
        padding-top: ${height ? 0 : "56.25%"};
        overflow: hidden;
        box-sizing: border-box;
        max-width: 100%;
        min-width: 200px;
        border-radius: 2px;
        box-shadow: 0 2px 15px rgb(0 0 0 / 10%);
        z-index: 0;
        direction: ltr;
        ${height ? `height: ${typeof height === "number" ? `${height}px` : height}` : ""}
        ${isEmbed &&
        css`
          height: 100%;
          padding-top: initial;
        `}
      `}
      className={`${className ? `${className} ` : ""}fv-player-container`}
    >
      <div
        css={css`
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
        `}
        className="fv-player-container-inner"
      >
        <div
          css={css`
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            opacity: ${hasEnded ? 1 : 0};
            pointer-events: ${hasEnded ? "all" : "none"};
          `}
          className="fv-player-container-restart-icon-layer"
        >
          <div
            css={css`
              background-color: black;
              background-image: url("${cover}");
              filter: grayscale(0.8) brightness(0.7) blur(2px);
              background-size: cover;
              background-position: 50%;
              position: absolute;
              top: 0;
              left: 0;
              right: 0;
              bottom: 0;
              cursor: pointer;
              z-index: 5;
              opacity: ${hasEnded ? 1 : 0};
              pointer-events: ${hasEnded ? "all" : "none"};
              transition: 0.2s;
            `}
            onClick={() => {
              handleSeek(0)
              play(player)
            }}
          />
          <CentralPlayerIcon show={hasStarted ? !isPlaying : true} icon={"restart"} />
        </div>
        {((isLoaded && !isBuffering(player)) || !userHasAttemptedPlay) && (
          <DelayedRender>
            <div
              css={css`
                position: absolute;
                top: 0;
                left: 0;
                right: 0;
                bottom: 0;
                z-index: 7;
                pointer-events: none;
              `}
              className="fv-player-container-play-icon-layer"
            >
              <CentralPlayerIcon show={!hasStarted} icon={"play"} />
            </div>
          </DelayedRender>
        )}
        {userHasAttemptedPlay &&
          !wontPlayStatus &&
          (!isLoaded || isBuffering(player)) &&
          !hasEnded && (
            <div
              css={css`
                background-color: rgba(0, 0, 0, 0.75);
                position: absolute;
                top: 0;
                left: 0;
                right: 0;
                bottom: 0;
                display: grid;
                place-items: center;
                z-index: ${isLoaded ? 99 : 101};
              `}
              className="fv-player-container-buffering-indicator-layer"
            >
              <div
                css={css`
                  position: absolute;
                  top: calc(50% - 20px);
                `}
              >
                <Spinner size={40} />
              </div>
            </div>
          )}
        {cover && (
          <div
            css={css`
              background-color: black;
              position: absolute;
              top: 0;
              left: 0;
              right: 0;
              bottom: 0;
              cursor: pointer;
              z-index: 5;
              opacity: ${hasStarted ? 0 : 1};
              transition: 0.2s;
              background-image: url("${cover}");
              background-size: cover;
              background-position: 50%;
              pointer-events: none;
            `}
            className="fv-player-container-poster-layer"
          />
        )}
        {Object.keys(errorMessages).includes(status) && (
          <div
            css={css`
              background-color: black;
              background-image: url("${cover}");
              filter: grayscale(0.8) brightness(0.7);
              background-size: cover;
              background-position: 50%;
              position: absolute;
              top: 0;
              left: 0;
              right: 0;
              bottom: 0;
              cursor: default;
              z-index: 10;
              opacity: 1;
              transition: 0.2s;
              pointer-events: all;
              color: white;
              display: grid;
              place-items: center;
              padding: 20px;
              font-size: 14px;
              text-align: center;
            `}
            className="fv-player-container-status-layer"
          >
            <div
              css={css`
                border-radius: 5px;
                background: #333;
                padding: 13px;
                max-width: 240px;
              `}
              className="fv-player-container-status-messages"
            >
              {(errorMessages as any)[status]}
            </div>
          </div>
        )}
        {isLoaded && isPlayerReady(player) && (
          <div
            css={css`
              background: linear-gradient(rgba(0, 0, 0, 0.75), rgba(0, 0, 0, 0));
              border-bottom-left-radius: inherit;
              border-bottom-right-radius: inherit;
              position: absolute;
              top: 0;
              left: 0;
              color: rgba(255, 255, 255, 0.95);
              right: 0;
              transition: opacity 0.2s ease-in-out, transform 0.2s ease-in-out;
              z-index: 100;
              padding: 10px 10px 35px 10px;
              opacity: ${displayOverlay ? 1 : 0};
              transform: translateY(${displayOverlay ? 0 : -10}px);
              pointer-events: none;
              font-size: 14px;
            `}
            className="fv-player-container-name"
          >
            {data?.name}
          </div>
        )}
        {player && data && (
          <Controls
            isReady={isLoaded && isPlayerReady(player)}
            displayOverlay={!!displayOverlay}
            displayToolTips={!!displayToolTips}
            player={player}
            isPlaying={isPlaying}
            data={data}
            containerRef={containerRef}
            videoRef={videoRef}
            captionFns={{ availableCaptions, currentCaptionId, handleSetCurrentCaptionId }}
            displayCaptionControl={displayCaptionControl}
          />
        )}
        <div
          css={css`
            height: 100%;
            margin: auto;
            overflow: hidden;
            position: relative;
            width: 100%;
            box-sizing: inherit;
            display: flex;
          `}
          className="fv-player-video-layer"
        >
          {isPlayerReady(player) && showDebug && (
            <div
              css={css`
                font-size: 10px;
                position: absolute;
                top: 4px;
                right: 4px;
                opacity: 0.7;
                background: black;
                color: white;
                z-index: 101;
                padding: 2px;
              `}
            >
              <b>Available Qualities</b>
              <br />
              {availableQualities.map((el, i) => (
                <React.Fragment key={i}>
                  {JSON.stringify(el)} <br />
                </React.Fragment>
              ))}
              <br />
              <b>Available Text Tracks</b>
              <br />
              {availableCaptions.map((el, i) => (
                <React.Fragment key={i}>
                  {JSON.stringify(el)} <br />
                </React.Fragment>
              ))}
              <br />
              <b>Is Buffering</b>: {isBuffering(player) ? "TRUE" : "FALSE"}
              <br />
              <b>Status</b>: {status}
              <br />
              <b>Time</b>: {getCurrentTime(player)}
              <br />
              <b>Volume</b>: {volume}
              <br />
              <b>Data</b>: {JSON.stringify(data, null, 2)}
              <br />
              {/*<b>Current quality</b>: {JSON.stringify(player.getQualityFor("video"), null, 2)}*/}
            </div>
          )}
          <video
            ref={videoRef}
            onClick={handleClick}
            autoPlay={autoPlay}
            loop={loop}
            css={css`
              all: unset;
              display: block;
              max-height: 100%;
              max-width: 100%;
              cursor: ${!hasStarted || displayOverlay ? "pointer" : "none"};
              width: 100%;
              margin: auto;
              object-fit: contain;
            `}
            className="fv-player-video"
            playsInline
          />
        </div>
      </div>
      <Captions
        player={player}
        availableCaptions={availableCaptions}
        currentCaptionId={currentCaptionId}
      />
      {showContextMenu && (
        <div
          css={css`
            all: unset;
            position: absolute;
            z-index: 9999;
          `}
          style={{ top: yPos, left: xPos }}
        >
          <ToolTip
            content={
              <div>
                <div
                  css={css`
                    width: 150px;
                    display: block;
                    cursor: pointer;
                    padding: 6px 12px;
                    font-size: 13px;
                    font-weight: 600;
                    &:hover {
                      background: #666;
                      border-radius: 4px;
                    }
                  `}
                  onClick={e => {
                    e.stopPropagation()
                    setShowDebug(!showDebug)
                    setShowContextMenu(false)
                  }}
                >
                  {showDebug ? "Hide" : "Display"} debug info
                </div>
                <div
                  css={css`
                    padding: 12px 12px 4px;
                    text-align: center;
                    font-size: 11px;
                    font-weight: bold;
                    margin-top: 6px;
                    color: #aaa;
                    border-top: 1px solid #666;
                  `}
                >
                  <div
                    css={css`
                      padding-bottom: 4px;
                    `}
                  >
                    Powered by Fileboy
                  </div>
                  <img
                    css={css`
                      display: inline-block;
                      width: 60px;
                    `}
                    src="https://api.fileboy.io/files/get/4158ff65-f098-4348-ba8c-98fef66d00ad"
                  />
                </div>
              </div>
            }
            placement="bottom-start"
            interactive
            visible
          >
            <div />
          </ToolTip>
        </div>
      )}
    </div>
  )
}

function DelayedRender({ children, timeoutMs }: { children: React.ReactNode; timeoutMs?: number }) {
  const [render, setRender] = useState(false)

  useEffect(() => {
    const timeout = setTimeout(() => setRender(true), timeoutMs ?? 500)

    return () => clearTimeout(timeout)
  }, [])

  return <React.Fragment>{render && children}</React.Fragment>
}
